/**
 * Additional Document Service
 * Handles API calls for managing additional documents
 */

const API_BASE_URL = process.env.REACT_APP_API_URL;

class AdditionalDocumentService {
  /**
   * Save a new additional document
   * @param {Object} documentData - Document data to save
   * @param {string} documentData.orgId - Organization ID
   * @param {string} documentData.appId - Application ID (optional)
   * @param {string} documentData.assessmentId - Assessment ID (required for assessor type)
   * @param {string} documentData.documentName - Name of the document
   * @param {string} documentData.targetType - Target type (lab/assessor/do)
   * @param {string} documentData.description - Document description
   * @param {Object} documentData.fileData - File upload data from FileUploadComponent
   * @param {string} documentData.uploadedBy - User who uploaded the document
   * @returns {Promise<Object>} API response
   */
  async saveDocument(documentData) {
    try {
      console.log('📄 Saving additional document:', documentData);

      // Prepare the request payload
      const payload = {
        orgId: documentData.orgId,
        appId: documentData.appId || null,
        documentName: documentData.documentName,
        targetType: documentData.targetType,
        description: documentData.description || '',
        fileName: documentData.fileData?.fileName || documentData.fileData?.originalName,
        fileKey: documentData.fileData?.key,
        fileSize: documentData.fileData?.fileSize,
        uploadedBy: documentData.uploadedBy || 'system',
        assessmentId: documentData.assessmentId || null,
        uploadDate: new Date().toISOString()
      };


      console.log('📄 Final payload for save:', payload);

      const response = await fetch(`${API_BASE_URL}/additional-documents`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to save document');
      }

      console.log('✅ Document saved successfully:', result);
      return {
        success: true,
        data: result.data || result,
        message: result.message || 'Document saved successfully'
      };
    } catch (error) {
      console.error('❌ Error saving document:', error);
      return {
        success: false,
        error: error.message || 'Failed to save document',
        data: null
      };
    }
  }

  /**
   * Get additional documents for an organization/application
   * @param {Object} filters - Filter criteria
   * @param {string} filters.orgId - Organization ID
   * @param {string} filters.appId - Application ID (optional)
   * @param {string} filters.assessmentId - Assessment ID (optional, for assessor type)
   * @param {string} filters.targetType - Target type filter (optional)
   * @returns {Promise<Object>} API response with documents array
   */
  async getDocuments(filters = {}) {
    try {
      console.log('📄 Loading additional documents with filters:', filters);

      // Build query parameters
      const queryParams = new URLSearchParams();
      if (filters.orgId) queryParams.append('orgId', filters.orgId);
      if (filters.appId) queryParams.append('appId', filters.appId);
      if (filters.targetType) queryParams.append('targetType', filters.targetType);
      
      // Add assessmentId for assessor type requests
      if (filters.assessmentId) {
        queryParams.append('assessmentId', filters.assessmentId);
        console.log('📄 Adding assessmentId to query params:', filters.assessmentId);
      }

      const finalUrl = `${API_BASE_URL}/additional-documents?${queryParams.toString()}`;
      console.log('📄 Final GET URL:', finalUrl);

      const response = await fetch(finalUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to load documents');
      }

      console.log(`✅ Loaded ${result.data?.length || 0} documents`);
      return {
        success: true,
        data: result.data || [],
        message: result.message
      };
    } catch (error) {
      console.error('❌ Error loading documents:', error);
      return {
        success: false,
        error: error.message || 'Failed to load documents',
        data: []
      };
    }
  }

  /**
   * Update an existing additional document
   * @param {string} documentId - Document ID to update
   * @param {Object} documentData - Updated document data
   * @param {string} documentData.assessmentId - Assessment ID (required for assessor type)
   * @param {string} documentData.documentName - Name of the document
   * @param {string} documentData.targetType - Target type (lab/assessor/do)
   * @param {string} documentData.description - Document description
   * @param {Object} documentData.fileData - File upload data from FileUploadComponent
   * @returns {Promise<Object>} API response
   */
  async updateDocument(documentId, documentData) {
    try {
      console.log('📄 Updating document:', documentId, documentData);

      // Prepare the request payload
      const payload = {
        documentName: documentData.documentName,
        targetType: documentData.targetType,
        description: documentData.description || '',
        fileName: documentData.fileData?.fileName || documentData.fileData?.originalName,
        fileKey: documentData.fileData?.key,
        fileSize: documentData.fileData?.fileSize,
        assessmentId: documentData.assessmentId || null,
        updatedDate: new Date().toISOString()
      };

      console.log('📄 Final payload for update:', payload);

      const response = await fetch(`${API_BASE_URL}/additional-documents/${documentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to update document');
      }

      console.log('✅ Document updated successfully:', result);
      return {
        success: true,
        data: result.data || result,
        message: result.message || 'Document updated successfully'
      };
    } catch (error) {
      console.error('❌ Error updating document:', error);
      return {
        success: false,
        error: error.message || 'Failed to update document',
        data: null
      };
    }
  }

  /**
   * Delete an additional document
   * @param {string} documentId - Document ID to delete
   * @returns {Promise<Object>} API response
   */
  async deleteDocument(documentId) {
    try {
      console.log('📄 Deleting document:', documentId);

      const response = await fetch(`${API_BASE_URL}/additional-documents/${documentId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to delete document');
      }

      console.log('✅ Document deleted successfully');
      return {
        success: true,
        data: result.data,
        message: result.message || 'Document deleted successfully'
      };
    } catch (error) {
      console.error('❌ Error deleting document:', error);
      return {
        success: false,
        error: error.message || 'Failed to delete document',
        data: null
      };
    }
  }

  /**
   * Get documents by target type for a specific organization
   * @param {string} orgId - Organization ID
   * @param {string} targetType - Target type (lab/assessor/do)
   * @returns {Promise<Object>} API response with filtered documents
   */
  async getDocumentsByTargetType(orgId, targetType) {
    return this.getDocuments({ orgId, targetType });
  }

  /**
   * Get document statistics for an organization
   * @param {string} orgId - Organization ID
   * @returns {Promise<Object>} API response with statistics
   */
  async getDocumentStats(orgId) {
    try {
      console.log('📊 Loading document statistics for org:', orgId);

      const response = await fetch(`${API_BASE_URL}/additional-documents/stats/${orgId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to load document statistics');
      }

      console.log('✅ Document statistics loaded:', result);
      return {
        success: true,
        data: result.data || {},
        message: result.message
      };
    } catch (error) {
      console.error('❌ Error loading document statistics:', error);
      return {
        success: false,
        error: error.message || 'Failed to load document statistics',
        data: {
          total: 0,
          byTargetType: {
            lab: 0,
            assessor: 0,
            do: 0
          }
        }
      };
    }
  }

  /**
   * Bulk delete documents
   * @param {Array<string>} documentIds - Array of document IDs to delete
   * @returns {Promise<Object>} API response
   */
  async bulkDeleteDocuments(documentIds) {
    try {
      console.log('📄 Bulk deleting documents:', documentIds);

      const response = await fetch(`${API_BASE_URL}/additional-documents/bulk-delete`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify({ documentIds }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to delete documents');
      }

      console.log(`✅ Successfully deleted ${documentIds.length} documents`);
      return {
        success: true,
        data: result.data,
        message: result.message || `Successfully deleted ${documentIds.length} documents`
      };
    } catch (error) {
      console.error('❌ Error bulk deleting documents:', error);
      return {
        success: false,
        error: error.message || 'Failed to delete documents',
        data: null
      };
    }
  }

  /**
   * Search documents by name or description
   * @param {Object} searchParams - Search parameters
   * @param {string} searchParams.orgId - Organization ID
   * @param {string} searchParams.query - Search query
   * @param {string} searchParams.targetType - Target type filter (optional)
   * @returns {Promise<Object>} API response with search results
   */
  async searchDocuments(searchParams) {
    try {
      console.log('🔍 Searching documents:', searchParams);

      const queryParams = new URLSearchParams();
      queryParams.append('orgId', searchParams.orgId);
      queryParams.append('query', searchParams.query);
      if (searchParams.targetType) {
        queryParams.append('targetType', searchParams.targetType);
      }

      const response = await fetch(`${API_BASE_URL}/additional-documents/search?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to search documents');
      }

      console.log(`✅ Found ${result.data?.length || 0} matching documents`);
      return {
        success: true,
        data: result.data || [],
        message: result.message
      };
    } catch (error) {
      console.error('❌ Error searching documents:', error);
      return {
        success: false,
        error: error.message || 'Failed to search documents',
        data: []
      };
    }
  }
}

// Create and export a singleton instance
export const additionalDocumentService = new AdditionalDocumentService();

// Also export the class for potential custom instances
export default AdditionalDocumentService;